import { useState, useEffect, useCallback } from "react";
import { api } from "@/api";
import { FISCAL_YEARS } from "@/constants/modules";
import { ArrowLeft, BarChart3, Info, AlertCircle } from "lucide-react";

export default function TaxRatesSubModule({ year: initialYear, setYear: setParentYear, onBack }) {
  const [tab, setTab] = useState("federal");
  const [year, setYear] = useState(initialYear);
  const [rates, setRates] = useState(null);
  const [sourceYear, setSourceYear] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/tax-lines/rates", { params: { tax_year: year } });
      setRates(r.data?.rates || null);
      setSourceYear(r.data?.source_year || null);
    } catch { setRates(null); setSourceYear(null); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleYearChange = (y) => {
    const v = parseInt(y);
    setYear(v);
    setParentYear?.(v);
  };

  const RateTable = ({ data, color, testId }) => (
    <div className="bg-white border rounded-xl overflow-hidden">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-2 font-medium text-gray-600">Tranche de revenu imposable</th>
            <th className="text-center px-4 py-2 font-medium text-gray-600">Taux marginal</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Impot cumulatif</th>
          </tr>
        </thead>
        <tbody>
          {(data || []).map((r, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
              <td className="px-4 py-2.5">{r.bracket}</td>
              <td className={`px-4 py-2.5 text-center font-bold ${color}`}>{r.rate}</td>
              <td className="px-4 py-2.5 text-gray-500 text-xs">{r.cumul}</td>
            </tr>
          ))}
          {!data?.length && <tr><td colSpan={3} className="text-center text-gray-400 py-6 text-sm">Donnees indisponibles</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const CreditTable = ({ data }) => (
    <div className="bg-white border rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-2 font-medium text-gray-600">Element</th>
            <th className="text-right px-4 py-2 font-medium text-gray-600">Montant</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Note</th>
          </tr>
        </thead>
        <tbody>
          {(data || []).map((c, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
              <td className="px-4 py-2.5">{c.item}</td>
              <td className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">{c.amount}</td>
              <td className="px-4 py-2.5 text-xs text-gray-400">{c.note}</td>
            </tr>
          ))}
          {!data?.length && <tr><td colSpan={3} className="text-center text-gray-400 py-6 text-sm">Donnees indisponibles</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="tax-rates-submodule">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700" data-testid="tax-rates-back">
          <ArrowLeft className="w-4 h-4" /> Retour aux lignes
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Taux d'imposition marginaux {year}
          </h1>
        </div>
        <select value={year} onChange={e => handleYearChange(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm" data-testid="rates-year-select">
          {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {sourceYear && sourceYear !== year && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 mb-4 text-xs flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Les taux {year} ne sont pas encore publies — affichage des taux {sourceYear}.</span>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-800 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium mb-1">Cheminement fiscal</p>
          <p className="text-xs leading-relaxed">Revenus - Deductions = Revenu net - Ajustements = Revenu imposable &rarr; Impot selon les taux &rarr; Moins credits non remboursables &rarr; Annexes et ajustements (K, FSS, etc.) &rarr; Impot net &rarr; Moins retenues a la source &rarr; Moins credits remboursables = Resultat final.</p>
          {rates?.note && <p className="text-xs mt-1 opacity-75">{rates.note}</p>}
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setTab("federal")} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === "federal" ? "bg-blue-600 text-white" : "bg-white border hover:bg-gray-50"}`} data-testid="rates-tab-federal">Federal</button>
        <button onClick={() => setTab("provincial")} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === "provincial" ? "bg-indigo-600 text-white" : "bg-white border hover:bg-gray-50"}`} data-testid="rates-tab-provincial">Provincial (Quebec)</button>
        <button onClick={() => setTab("combines")} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === "combines" ? "bg-emerald-600 text-white" : "bg-white border hover:bg-gray-50"}`} data-testid="rates-tab-combined">Combines Fed+QC</button>
      </div>

      {tab === "federal" && (
        <div className="space-y-4">
          <div>
            <div className="px-1 py-2"><h3 className="text-sm font-semibold text-blue-800">Paliers d'imposition federaux {year}</h3></div>
            <RateTable data={rates?.federal_brackets} color="text-blue-700" testId="federal-rates-table" />
          </div>
          <div>
            <div className="px-1 py-2"><h3 className="text-sm font-semibold text-blue-800">Montants de reference — Credits federaux {year}</h3></div>
            <CreditTable data={rates?.federal_credits} />
          </div>
        </div>
      )}

      {tab === "provincial" && (
        <div className="space-y-4">
          <div>
            <div className="px-1 py-2"><h3 className="text-sm font-semibold text-indigo-800">Paliers d'imposition Quebec {year}</h3></div>
            <RateTable data={rates?.provincial_brackets} color="text-indigo-700" testId="provincial-rates-table" />
          </div>
          <div>
            <div className="px-1 py-2"><h3 className="text-sm font-semibold text-indigo-800">Montants de reference — Credits provinciaux {year}</h3></div>
            <CreditTable data={rates?.provincial_credits} />
          </div>
        </div>
      )}

      {tab === "combines" && (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800">
            <p className="font-semibold mb-1">Abattement du Quebec (16,5 %)</p>
            <p className="text-xs">Les residents du Quebec beneficient d'un abattement de 16,5 % sur l'impot federal de base. Les taux ci-dessous tiennent compte de cette reduction.</p>
          </div>
          <div>
            <div className="px-1 py-2"><h3 className="text-sm font-semibold text-emerald-800">Taux marginaux combines (residents du Quebec) {year}</h3></div>
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="combined-rates-table">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Tranche</th>
                    <th className="text-center px-3 py-2 font-medium text-blue-600">Fed. effectif</th>
                    <th className="text-center px-3 py-2 font-medium text-indigo-600">Quebec</th>
                    <th className="text-center px-3 py-2 font-medium text-emerald-700 bg-emerald-50">Combine</th>
                  </tr>
                </thead>
                <tbody>
                  {(rates?.combined || []).map((r, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-xs">{r.bracket}</td>
                      <td className="px-3 py-2.5 text-center text-blue-600">{r.fedEff}</td>
                      <td className="px-3 py-2.5 text-center text-indigo-600">{r.qc}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-emerald-700 bg-emerald-50">{r.combined}</td>
                    </tr>
                  ))}
                  {!rates?.combined?.length && (
                    <tr><td colSpan={4} className="text-center text-gray-400 py-6 text-sm">Donnees indisponibles</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4 text-center">
        Source : ARC / Revenu Quebec — Annee d'imposition {year}. A titre indicatif seulement. Consultez les formulaires officiels pour les calculs precis.
      </p>
    </div>
  );
}
